/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import 'moment/locale/tr';
import { toAbsoluteUrl } from 'src/_metronic/helpers';
import { translate } from 'src/locales/i18next/config';
import Translations from 'src/locales/i18next/translations/translation';
import { DSalesPerformaceParams, useChartList, useDashboardList, useInvoiceEntryCheck } from '../../services';
import '../../styles/style.scss';
import { Donut } from '../donut';
import Loading from '../loading';
import { SalesPerformance } from '../sales-performance';
import InvoiceEntryReminderModal from '../invoice-entry-reminder-modal';

moment.locale('tr');

const DashboardPage: FC = () => {
  const history = useHistory();
  const t = translate(Translations);
  const translation = 'pages.dashboard';
  const [salesDay, setSalesDay] = useState<DSalesPerformaceParams>('daily');
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  const onErrorDashboardFetch = (error: any) => {
    toast.error(error.response?.data?.detail || error?.message || 'Desi bilgisi gönderilirken bir hata oluştu.', { autoClose: 1500 });
  };

  const onErrorChartFetch = (error: any) => {
    toast.error(error.response?.data?.detail || error?.message || 'Desi bilgisi gönderilirken bir hata oluştu.', { autoClose: 1500 });
  };

  const { isLoading, isFetching, data } = useDashboardList(onErrorDashboardFetch);
  const { refetch: chartRefetch, isFetching: chartIsFetching } = useChartList({ ...{ operationFilter: salesDay } } as any, onErrorChartFetch);
  const { data: invoiceCheckData } = useInvoiceEntryCheck();

  useEffect(() => {
    const STORAGE_KEY = 'invoiceEntryReminderShown';
    const hasBeenShown = sessionStorage.getItem(STORAGE_KEY);

    if (invoiceCheckData?.hasPendingOrders && !hasBeenShown) {
      setShowInvoiceModal(true);
      sessionStorage.setItem(STORAGE_KEY, 'true');
    }
  }, [invoiceCheckData]);

  const handleChangeDays = (e: any) => {
    setSalesDay(e.target.value);
    setTimeout(() => {
      chartRefetch();
    }, 100);
  };

  const handleCloseInvoiceModal = () => {
    setShowInvoiceModal(false);
  };

  const chartData = useMemo(() => {
    if (salesDay === 'daily') return data?.oms?.dailyChart;
    if (salesDay === 'sevenDays') return data?.oms?.sevenDayChart;
    if (salesDay === 'thirtyDays') return data?.oms?.thirtyDayChart;
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesDay, data]);

  const futurePayments = useMemo(() => {
    return {
      firstPayment: {
        date: data?.escrow?.futureEstimatedEarningDates?.[0]?.futureEarningDate,
        amount: data?.escrow?.futureEstimatedEarningDates?.[0]?.totalPrice
      },
      secondPayment: {
        date: data?.escrow?.futureEstimatedEarningDates?.[1]?.futureEarningDate,
        amount: data?.escrow?.futureEstimatedEarningDates?.[1]?.totalPrice
      }
    };
  }, [data?.escrow?.futureEstimatedEarningDates]);

  if (isFetching || isLoading) return <Loading />;

  const lastTransferedMoneyOrder = data?.escrow.lastTransferedMoneyOrder;

  return (
    <>
      {/* idefix takipçi ve satıcı puanı */}
      <div className="d-flex align-items-center">
        <img
          alt="Logo"
          src={data?.vendorInfo?.logoUrl ? data?.vendorInfo?.logoUrl : toAbsoluteUrl('/media/avatars/blank.png')}
          className="img-fluid w-100px h-100px"
          style={{ objectFit: 'cover' }}
        />
        <div className="mx-10">
          <div className="fs-3 fw-semibold">{data?.vendorInfo?.title}</div>
          <div className="d-flex">
            <div className="bg-secondary p-3 mt-5 fs-7 text-nowrap">{Number(data?.vendorCountInfo?.followCount)?.toLocaleString('tr-TR')} Takipçi</div>
            <div className="bg-success p-3 mt-5 mx-5 text-white fs-7 text-nowrap cursor-pointer" onClick={() => history.push('/dashboard/seller-evaluation')}>
              Satıcı Puanı : {data?.vendorInfo?.vendorScore}/10
            </div>
          </div>
        </div>
      </div>
      {/* Sipariş Performansı, Bugünkü Satış ve Duyurular */}
      <div className="row mt-5">
        <div className="col-md-8 mb-5 ">
          <div className="row bg-white p-5 h-100 align-items-center">
            <div className="col-md-8 mb-5">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="fs-5 fw-semibold text-gray-900 ">Sipariş Performansı</div>
                <select className="form-select form-select-sm form-select-control w-150px" disabled={chartIsFetching} onChange={handleChangeDays}>
                  <option value={'daily'}>Bugün</option>
                  <option value={'sevenDays'}>7 Gün</option>
                  <option value={'thirtyDays'}>30 Gün</option>
                </select>
              </div>
              <div className="h-250px position-relative">
                <SalesPerformance chartData={chartData} salesDay={salesDay} />
              </div>
            </div>

            <div className="col-md-4 mb-5">
              <div className="fs-5 fw-semibold text-gray-900 mb-3">Bugünkü Satış</div>
              <div className="mb-10">
                <div className="d-flex align-items-center">
                  <div className="fw-bolder">{data?.oms?.salesPerformance?.dailySaleCount}</div>
                  <div className="fw-normal mx-2">%{data?.oms?.salesPerformance?.dailySaleCountChangeRate}</div>
                </div>
                <div className="d-flex align-items-center">
                  <div className="fw-normal">Toplam Satılan Tutar</div>
                  <div className="fw-bolder mx-2">{Number(data?.oms?.salesPerformance?.dailySaleTotal)?.toLocaleString('tr-TR')} TL</div>
                </div>
              </div>
              <div className="fs-5 fw-semibold text-gray-900 mb-3">7 Günlük Satış</div>
              <div className="mb-10">
                <div className="d-flex align-items-center">
                  <div className="fw-bolder">{data?.oms?.salesPerformance?.sevenDaySaleCount}</div>
                  <div className="fw-normal mx-2">%{data?.oms?.salesPerformance?.sevenDaySaleCountChangeRate}</div>
                </div>
                <div className="d-flex align-items-center">
                  <div className="fw-normal">Toplam Satılan Tutar</div>
                  <div className="fw-bolder mx-2">{Number(data?.oms?.salesPerformance?.sevenDaySaleTotal)?.toLocaleString('tr-TR')} TL</div>
                </div>
              </div>
              <div className="fs-5 fw-semibold text-gray-900 mb-3">30 Günlük Satış</div>
              <div className="mb-0">
                <div className="d-flex align-items-center">
                  <div className="fw-bolder">{data?.oms?.salesPerformance?.thirtyDaySaleCount}</div>
                  <div className="fw-normal mx-2">%{data?.oms?.salesPerformance?.thirtyDaySaleCountChangeRate}</div>
                </div>
                <div className="d-flex align-items-center">
                  <div className="fw-normal">Toplam Satılan Tutar</div>
                  <div className="fw-bolder mx-2">{Number(data?.oms?.salesPerformance?.thirtyDaySaleTotal)?.toLocaleString('tr-TR')} TL</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4 ps-md-10 mb-5">
          <div className="row bg-white px-5 h-100 ">
            <div className="d-flex align-items-center mb-5">
              <div className="fs-5 fw-semibold text-gray-900  d-flex align-items-center">Bildirimler</div>
              <span className="mx-2 ">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
              </span>
            </div>
            <div
              className="d-flex w-100  jusitfy-content-between cursor-pointer mb-3 mb-sm-0"
              onClick={() =>
                history.push({
                  pathname: '/notifications',
                })
              }
            >
              <div className="position-relative">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="feather feather-mail"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                {data?.vendorCountInfo && data?.vendorCountInfo?.unreadMessageCount > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle p-1 bg-danger border border-light rounded-circle"></span>
                )}{' '}
              </div>
              <div className="mx-7 ">
                {' '}
                {t(`${translation}.notificationsSection.unRead`)} {data?.vendorCountInfo && data?.vendorCountInfo?.unreadMessageCount}{' '}
                {t(`${translation}.notificationsSection.new`)} {t(`${translation}.notificationsSection.message`)}
              </div>
            </div>
            <div
              className="d-flex w-100 cursor-pointer mb-3 mb-sm-0"
              onClick={() =>
                history.push({
                  pathname: '/products/questions',
                  state: {
                    waitingForAnswer: true,
                  },
                })
              }
            >
              <div className="position-relative">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="feather feather-mail"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                {data?.vendorCountInfo && data?.vendorCountInfo?.unreadProductQuestion > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle p-1 bg-danger border border-light rounded-circle"></span>
                )}{' '}
              </div>
              <div className="mx-7">
                {t(`${translation}.notificationsSection.unAnswered`)} {data?.vendorCountInfo && data?.vendorCountInfo?.unreadProductQuestion}{' '}
                {t(`${translation}.notificationsSection.productQuestion`)}
              </div>
            </div>
            <div
              className="d-flex w-100 cursor-pointer mb-3 mb-sm-0"
              onClick={() =>
                history.push({
                  pathname: '/orders/orders-management/all',
                  state: {
                    delayedShipment: 1,
                  },
                })
              }
            >
              <div className="position-relative">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="9" cy="21" r="1"></circle>
                  <circle cx="20" cy="21" r="1"></circle>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                </svg>
                {data?.vendorCountInfo && data?.vendorCountInfo?.lateShipmentCount > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle p-1 bg-danger border border-light rounded-circle"></span>
                )}{' '}
              </div>
              <div className="text-center mt-1"></div>

              <div className="mx-7">
                {data?.vendorCountInfo && data?.vendorCountInfo?.lateShipmentCount} {t(`${translation}.notificationsSection.delayedOrders`)}
              </div>
            </div>
            <div
              className="d-flex w-100 mb-2 cursor-pointer mb-3 mb-sm-0"
              onClick={() =>
                history.push({
                  pathname: '/orders/refund/waiting-approve',
                })
              }
            >
              <div className="position-relative">
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="css-i6dzq1"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                {data?.vendorCountInfo && data?.vendorCountInfo?.waitingClaimResponse > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle p-1 bg-danger border border-light rounded-circle"></span>
                )}{' '}
              </div>
              <div className="mx-7">
                {data?.vendorCountInfo && data?.vendorCountInfo?.waitingClaimResponse} {t(`${translation}.notificationsSection.pendingOrders`)}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Sipariş Aksiyonları, Ürün Aksiyonları, Fatura Aksiyonları ve Muhasebe */}
      <div className="row mt-5">
        <div className="col-xl-8 mb-5">
          <div className="row h-100">
            <div className="col-md-4 mb-5">
              <div className="row bg-white h-100 p-5 mb-5">
                <div className="fs-5 fw-semibold text-gray-900 mb-5">Sipariş Aksiyonlarım</div>
                <div
                  className="d-flex justify-content-between align-items-center mb-3 cursor-pointer bg-hover-light-primary border border-2 rounded border-primary border-hover dashboard-animated"
                  onClick={() => history.push('/orders/orders-management/new')}
                >
                  <div>Yeni Siparişler</div>
                  <div>{data?.oms?.shipmentActions?.new_shipment_count?.toLocaleString('tr-TR')}</div>
                </div>
                <div
                  className="d-flex justify-content-between align-items-center mb-3 cursor-pointer bg-hover-light-primary border border-2 rounded border-primary border-hover dashboard-animated"
                  onClick={() =>
                    history.push({
                      pathname: '/orders/orders-management/all',
                      state: {
                        delayedShipment: 1,
                      },
                    })
                  }
                >
                  <div>Geciken Siparişler</div>
                  <div>{data?.oms?.shipmentActions?.late_shipment_count?.toLocaleString('tr-TR')}</div>
                </div>
                <div
                  className="d-flex justify-content-between align-items-center mb-3 cursor-pointer bg-hover-light-primary border border-2 rounded border-primary border-hover dashboard-animated"
                  onClick={() => history.push('/orders/refund/waiting-approve')}
                >
                  <div>Bekleyen İadeler</div>
                  <div>{data?.oms?.shipmentActions?.waiting_claim_response?.toLocaleString('tr-TR')}</div>
                </div>
                <div
                  className="d-flex justify-content-between align-items-center mb-3 cursor-pointer bg-hover-light-primary border border-2 rounded border-primary border-hover dashboard-animated"
                  onClick={() =>
                    history.push({
                      pathname: '/orders/questions',
                      state: {
                        unanswered: true,
                      },
                    })
                  }
                >
                  <div>Sipariş Soruları</div>
                  <div>{data?.oms?.shipmentActions?.unanswer_shipment_question_count?.toLocaleString('tr-TR')}</div>
                </div>
              </div>
            </div>
            <div className="col-md-4 ps-md-10 mb-5">
              <div className="row bg-white h-100 p-5">
                <div className="fs-5 fw-semibold text-gray-900 mb-5">Ürün Aksiyonlarım</div>
                <div
                  className="d-flex justify-content-between align-items-center mb-3 cursor-pointer bg-hover-light-primary border border-2 rounded border-primary border-hover dashboard-animated"
                  onClick={() => history.push('/products/my-products/products-missing-information')}
                >
                  <div>Eksik Bilgili Ürünler</div>
                  <div>{data?.vendorCountInfo?.missingInfoCount?.toLocaleString('tr-TR')}</div>
                </div>
                <div
                  className="d-flex justify-content-between align-items-center mb-3 cursor-pointer bg-hover-light-primary border border-2 rounded border-primary border-hover dashboard-animated"
                  onClick={() =>
                    history.push({
                      pathname: '/inventory/all',
                      state: {
                        tabName: 'tabOutOfStock',
                      },
                    })
                  }
                >
                  <div>Stoğu Olmayan Ürünler</div>
                  <div>{data?.vendorCountInfo?.outOfStock?.toLocaleString('tr-TR')}</div>
                </div>
                <div
                  className="d-flex justify-content-between align-items-center mb-3 cursor-pointer bg-hover-light-primary border border-2 rounded border-primary border-hover dashboard-animated"
                  onClick={() =>
                    history.push({
                      pathname: '/inventory/all',
                      state: {
                        tabName: 'tabLowStock',
                      },
                    })
                  }
                >
                  <div>Stoğu Azalan Ürünler</div>
                  <div>{data?.vendorCountInfo?.tabLowStock?.toLocaleString('tr-TR')}</div>
                </div>
                <div
                  className="d-flex justify-content-between align-items-center mb-3 cursor-pointer bg-hover-light-primary border border-2 rounded border-primary border-hover dashboard-animated"
                  onClick={() =>
                    history.push({
                      pathname: '/products/questions',
                      state: {
                        waitingForAnswer: true,
                      },
                    })
                  }
                >
                  <div>Ürün Soruları</div>
                  <div>{data?.vendorCountInfo?.unreadProductQuestion?.toLocaleString('tr-TR')}</div>
                </div>
              </div>
            </div>
            <div className="col-md-4 ps-md-10  mb-5">
              <div className="row bg-white h-100 p-5">
                <div className="fs-5 fw-semibold text-gray-900 mb-5">Fatura Aksiyonları</div>
                <div className="d-flex align-items-center justify-content-center">
                  <div className="justify-content-center align-items-center d-flex flex-column">
                    <div className="badge badge-light-primary p-8 mb-5 fs-1">{data?.oms?.invoiceActions?.count?.toLocaleString('tr-TR')}</div>
                    <div>Platforma Bildirilmemiş Fatura</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-4 ps-xl-10 mb-5">
          <div className="row bg-white p-5 mb-5">
            <div className="d-flex align-items-center justify-content-between mb-5">
              <div className="fs-5 fw-semibold text-gray-900  d-flex align-items-center">Muhasebe</div>

              <div className="text-gray-600 text-decoration-underline fs-7 cursor-pointer" onClick={() => history.push('/finance/vesting')}>
                Detayları Gör
              </div>
            </div>
            <div className="row">
              <div className="col-md-12 ">
                {lastTransferedMoneyOrder ? (
                  <div className="mb-5">
                    <div>Son Ödenen Hak Ediş</div>
                    <div className="fw-bolder">
                      {Number(lastTransferedMoneyOrder?.amount)?.toLocaleString('tr-TR')} TL - {moment(lastTransferedMoneyOrder?.date).format('DD.MM.YYYY HH:mm:ss')}
                    </div>
                  </div>
                ) : null}
                <div className="mb-5">
                  <div>Gelecek Ödemeler</div>
                  <div className="mb-3">
                    <div>
                      {futurePayments.firstPayment.date &&
                        moment(futurePayments.firstPayment.date)?.format('DD MMMM') + ' Hak Ediş Ödemesi'}
                    </div>
                    <div>
                      {futurePayments.firstPayment.amount ? (
                        <>
                          Tahmini Tutar <span className="fw-bolder">{Number(futurePayments.firstPayment.amount).toLocaleString('tr-TR')} TL</span>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <div className="mb-3">
                    <div>
                      {futurePayments.secondPayment.date &&
                        moment(futurePayments.secondPayment.date)?.format('DD MMMM') + ' Hak Ediş Ödemesi'}
                    </div>
                    <div>
                      {futurePayments.secondPayment.amount ? (
                        <>
                          Tahmini Tutar <span className="fw-bolder">{Number(futurePayments.secondPayment.amount).toLocaleString('tr-TR')} TL</span>
                        </>
                      ) : null}
                    </div>
                  </div>
                  {(futurePayments.firstPayment.amount || futurePayments.secondPayment.amount) && (
                    <div className="text-danger fs-7 mt-2">
                      *Oluşabilecek iadeler, düzenlenecek kargo faturası, hizmet bedeli ve stopaj kesintilerine istinaden bu tutarlar değişebilmektedir.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-12 bg-white p-5">
          <div className="fs-5 fw-semibold text-gray-900 mb-3">Çok Satanlar</div>
          <div className="d-lg-flex align-items-center overflow-auto ">
            <div className="d-lg-flex flex-nowrap mb-5">
              {data?.oms?.bestSellers?.map((bestSeller: any, idx: number) => (
                <div className="card w-lg-200px me-5 mb-5" key={idx}>
                  <div className="bg-white rounded h-200px position-relative">
                    <img alt="Logo" src={bestSeller?.src} className="w-100 h-200px" style={{ objectFit: 'contain' }} />
                    <div className="bg-white p-2 position-absolute left-0 top-0 mt-3 mx-3 fs-8 fw-normal shadow">Son 30 Günde {bestSeller?.count} Adet Satıldı</div>
                  </div>
                  <div className="fw-normal mt-3 min-h-40px overflow-hidden">{bestSeller?.title}</div>
                  <div className="fw-normal mt-3">Toplam Sipariş Tutarı</div>
                  <div className="fw-bolder ">{Number(bestSeller?.total_price)?.toLocaleString('tr-TR')} TL</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="row mt-10">
        <div className="col-md-6 mb-5 ">
          <div className="row bg-white p-5 h-100 align-items-center">
            <div className="col-md-12">
              <div className="table-responsive">
                <table className="table align-middle bg-lighten">
                  <thead>
                    <tr className="fw-semibold fs-6 text-gray-900">
                      <th className="w-500px ps-3">Çok Satan Markalar</th>
                      <th className="">Adet</th>
                      <th className="min-w-50px">Tutar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.oms?.bestSellerBrands?.slice(0, 3)?.map((sellerBrand: any) => (
                      <tr>
                        <td className="w-500px ps-3">{sellerBrand?.name?.length > 24 ? `${sellerBrand?.name?.substring(0, 24)}…` : sellerBrand?.name}</td>
                        <td>{sellerBrand?.count}</td>
                        <td>{Number(sellerBrand?.totalPrice)?.toLocaleString('tr-TR')} TL</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="col-md-12">
              <div className="table-responsive">
                <table className="table align-middle bg-lighten">
                  <thead>
                    <tr className="fw-semibold fs-6 text-gray-900">
                      <th className="w-500px ps-3">Çok Satan Kategoriler</th>
                      <th className="">Adet</th>
                      <th className="min-w-50px">Tutar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.oms?.bestSellerCategories?.slice(0, 3)?.map((sellerCategories: any) => (
                      <tr>
                        <td className="w-500px ps-3">{sellerCategories?.name?.length > 24 ? `${sellerCategories?.name?.substring(0, 24)}…` : sellerCategories?.name}</td>
                        <td>{sellerCategories?.count}</td>
                        <td>{Number(sellerCategories?.totalPrice)?.toLocaleString('tr-TR')} TL</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-6 ps-md-10 mb-5">
          <div className="row bg-white p-5 h-100 ">
            <div className="d-flex justify-content-between mb-5">
              <div className="fs-5 fw-semibold text-gray-900 ">İptal Oranları</div>
              <div className="text-gray-600 text-decoration-underline fs-7 cursor-pointer" onClick={() => history.push('/reports/cancel')}>
                İptal Raporuna Git
              </div>
            </div>
            <Donut chartColor="primary" chartHeight="200px" chartData={data?.oms?.cancelCounts} />
          </div>
        </div>
      </div>
      <InvoiceEntryReminderModal
        show={showInvoiceModal}
        onHide={handleCloseInvoiceModal}
        orderCount={invoiceCheckData?.count || 0}
      />
    </>
  );
};

export default DashboardPage;
